// Tallennusavaimet ja niiden luku/kirjoitus. Avainten nimet ovat sopimus:
// ne on dokumentoitu docs/tietosuoja/tallennusavaimet.md:hen, eikä niitä
// muuteta ilman päätöslokikirjausta ja saman PR:n dokumentaatiopäivitystä.
//
// V2-T1 (PL-5): todistustunnuksen avain on cmp_proof_id. Vanha nimi
// cmp_visitor_id näytti ulkoisessa skannerissa seurantatunnisteelta, vaikka
// kyse on suostumuksen todistusketjun viitteestä. Vanha avain migroidaan
// uuteen nimeen kävijän selaimessa — kävijän todistustunnus ei katoa.

import type { Choices, StoredConsent } from './types';

/** Todistustunnuksen avain (V2-T1, PL-5). */
export const PROOF_ID_KEY = 'cmp_proof_id';

/** Ennen V2-T1:tä käytetty avain, josta arvo migroidaan uuteen. */
export const LEGACY_PROOF_ID_KEY = 'cmp_visitor_id';

export function consentKey(domain: string): string {
  return 'cmp_consent_' + domain;
}

export function consentIdKey(domain: string): string {
  return 'cmp_consent_id_' + domain;
}

export function gcmKey(domain: string): string {
  return 'cmp_gcm_' + domain;
}

export function patternsKey(domain: string): string {
  return 'cmp_patterns_' + domain;
}

/** localStorage-luku, joka ei kaadu (esim. Safarin yksityinen selaus). */
function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** localStorage-kirjoitus, joka ei kaadu. Palauttaa onnistuiko. */
function safeSet(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // localStorage ei käytettävissä
  }
}

/**
 * Suostumuksen oletusvoimassaolo päivinä (V2-T2). 12 kk on Traficomin
 * ohjeistuksen mukainen ylärajasuositus; per sivusto konfiguroitavissa
 * hallintapaneelissa (banner_configs.consent_expiry_days).
 */
export const DEFAULT_CONSENT_EXPIRY_DAYS = 365;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Konfiguroidun voimassaolon sanity-tarkistus (virheellinen → oletus). */
export function normalizeExpiryDays(days: number | null | undefined): number {
  if (typeof days !== 'number' || !isFinite(days) || days <= 0) {
    return DEFAULT_CONSENT_EXPIRY_DAYS;
  }
  return Math.floor(days);
}

/**
 * Raakatallenne ilman voimassaolotarkistusta. Käytä vain, kun vanhentunut
 * tallenne on tarkoitus nähdä (esim. voimassaolon arviointi); kävijäpolun
 * päätökset tehdään getValidConsent/getStoredChoices-funktioilla.
 */
export function getStoredConsent(domain: string): StoredConsent | null {
  try {
    return JSON.parse(localStorage.getItem(consentKey(domain)) as string);
  } catch {
    return null;
  }
}

/** Milloin tallennettu suostumus vanhenee (ms epoch). */
export function consentExpiresAt(stored: StoredConsent): number {
  if (typeof stored.exp === 'number' && isFinite(stored.exp)) return stored.exp;
  // Vaihetta 2 edeltävä tallenne: voimassaolo lasketaan oletuksella
  return (stored.ts || 0) + DEFAULT_CONSENT_EXPIRY_DAYS * DAY_MS;
}

/**
 * Onko tallennettu suostumus voimassa. Vanhentunut tai rikkinäinen tallenne
 * ei vapauta skriptejä eikä estä bannerin näyttämistä (V2-T2).
 */
export function isConsentValid(stored: StoredConsent | null, now?: number): boolean {
  if (!stored || !stored.choices || typeof stored.ts !== 'number') return false;
  return (now === undefined ? Date.now() : now) < consentExpiresAt(stored);
}

/** Voimassa oleva tallennettu suostumus tai null. */
export function getValidConsent(domain: string, now?: number): StoredConsent | null {
  const stored = getStoredConsent(domain);
  return isConsentValid(stored, now) ? stored : null;
}

/**
 * Voimassa olevan suostumuksen valinnat tai null (inline-blokkerin lukutapa).
 * Vanhentunut suostumus luetaan kuin suostumusta ei olisi — kaikki
 * ei-välttämätön pysyy estettynä, kunnes kävijä valitsee uudelleen.
 */
export function getStoredChoices(domain: string): Choices | null {
  const valid = getValidConsent(domain);
  return valid ? valid.choices ?? null : null;
}

/**
 * Tallentaa valinnan, sen voimassaolon päättymishetken ja konfiguraation
 * olennaisen version tunnisteen. expiryDays tulee sivuston konfiguraatiosta;
 * virheellinen tai puuttuva arvo → oletus 12 kk.
 */
export function storeConsent(
  domain: string,
  choices: Choices,
  action: string,
  expiryDays?: number | null,
  configFingerprint?: string | null,
): void {
  const ts = Date.now();
  const stored: StoredConsent = {
    choices,
    action,
    ts,
    exp: ts + normalizeExpiryDays(expiryDays) * DAY_MS,
  };
  // Konfiguraation olennaisen sisällön tunniste (V2-T3): kertoo, mitä vastaan
  // suostumus annettiin, jotta olennainen muutos johtaa uudelleenkysyntään.
  if (configFingerprint) stored.cv = configFingerprint;
  localStorage.setItem(consentKey(domain), JSON.stringify(stored));
}

/**
 * Siirtää vanhan cmp_visitor_id-avaimen arvon cmp_proof_id:hin ja poistaa
 * vanhan (V2-T1, PL-5). Ei luo uutta tunnusta eikä kirjoita mitään, jos
 * vanhaa avainta ei ole — uudelle kävijälle tämä on no-op, joten ennen
 * suostumusta ei synny uutta tallennusta.
 *
 * @returns migroitu tunnus, tai null jos migroitavaa ei ollut.
 */
export function migrateProofId(): string | null {
  const legacy = safeGet(LEGACY_PROOF_ID_KEY);
  if (!legacy) return null;

  const current = safeGet(PROOF_ID_KEY);
  if (!current) {
    // Arvo siirretään vasta kun kirjoitus onnistui — muuten tunnus katoaisi
    if (!safeSet(PROOF_ID_KEY, legacy)) return null;
  }
  safeRemove(LEGACY_PROOF_ID_KEY);
  return current || legacy;
}

/**
 * Suostumuksen todistustunnus (kävijän ja rekisterinpitäjän viite
 * suostumuslokin riviin). HUOM: luodaan vasta kun valinta lähetetään
 * (sendConsent) — ei sivulatauksessa.
 */
export function getProofId(): string {
  let id = safeGet(PROOF_ID_KEY);
  if (!id) id = migrateProofId();
  if (!id) {
    id = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    safeSet(PROOF_ID_KEY, id);
  }
  return id;
}

export function getConsentId(domain: string): string | null {
  return localStorage.getItem(consentIdKey(domain));
}

export function storeConsentId(domain: string, consentId: string): void {
  localStorage.setItem(consentIdKey(domain), consentId);
}
